// How the app writes numbers and dates (Tare Design System, "Numbers are the point"): money to the cent with commas,
// quantities with their unit, dates as a person writes them (Mon 19 Oct in tables, Monday 19 October in titles), never
// ISO on screen. Times are New York's, where Tare works.

const TZ = "America/New_York";

/** Today in New York, YYYY-MM-DD. */
export function today(now = new Date()): string {
  return now.toLocaleDateString("en-CA", { timeZone: TZ });
}

export function money(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "";
  return n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function qty(n: number | null | undefined, unit = "EA"): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "";
  return `${n.toLocaleString("en-US", { maximumFractionDigits: 3 })}${unit ? ` ${unit}` : ""}`;
}

/** A unit price or cost per EA, as written on the PO (up to 4 decimals, at least 3: the matching rule's precision). */
export function unitPrice(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "";
  return n.toLocaleString("en-US", { minimumFractionDigits: 3, maximumFractionDigits: 4 });
}

/** A calendar day ("2026-10-19") or a timestamp, as its New York date parts. */
function parts(value: string): Date | null {
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  // A bare day is that day wherever you are: noon UTC is the same date in New York.
  const d = day ? new Date(Date.UTC(+day[1], +day[2] - 1, +day[3], 12)) : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Mon 19 Oct (tables). */
export function shortDate(value: string | null | undefined): string {
  const d = value ? parts(value) : null;
  if (!d) return "";
  const f = (o: Intl.DateTimeFormatOptions) => d.toLocaleDateString("en-GB", { timeZone: TZ, ...o });
  return `${f({ weekday: "short" })} ${f({ day: "numeric" })} ${f({ month: "short" })}`;
}

/** Monday 19 October (titles and sentences). */
export function longDate(value: string | Date): string {
  const d = typeof value === "string" ? parts(value) : value;
  if (!d) return "";
  const f = (o: Intl.DateTimeFormatOptions) => d.toLocaleDateString("en-GB", { timeZone: TZ, ...o });
  return `${f({ weekday: "long" })} ${f({ day: "numeric" })} ${f({ month: "long" })}`;
}

/** Mon 19 Oct, 2:17pm (when something happened). */
export function when(value: string | null | undefined): string {
  const d = value ? parts(value) : null;
  if (!d) return "";
  const t = d.toLocaleTimeString("en-US", { timeZone: TZ, hour: "numeric", minute: "2-digit" }).replace(" ", "").toLowerCase();
  return `${shortDate(value)}, ${t}`;
}

/** "one", "two" ... "nine", then digits: counts at the start of a sentence read better as words. */
export function count(n: number): string {
  return ["No", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine"][n] ?? String(n);
}

export function plural(n: number, one: string, many = `${one}s`): string {
  return n === 1 ? one : many;
}
